import React, { FC } from 'react'
import { TitleController, Loading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  ResidentAppAsideSection,
  ResidentAppAsideText,
  ResidentAppBackLink,
  ResidentAppCard,
  ResidentAppView,
} from '@pandiafi/components'

import { areaAddressLabel } from './uiSelectors'
import type { CoreDayReservableArea } from '../../../common/validations/contractDayReservableAreasSchema'
import { DayReservableAreaModal } from './DayReservableAreaModal'
import { DayReservableAreasGroup } from './DayReservableAreasGroup'
import { DayReservableAreasDataLoader } from './DayReservableAreasDataLoader'

import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { ExtraServiceReservationsList } from 'components/ExtraServiceReservations'
import { usePermissions } from 'hooks/usePermissions'
import { ReservationRulesAndTermsLinks } from 'components'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'

type GroupedAreas = {
  id: string
  address: string
  buildingPartLocationDetails: string
  areas: CoreDayReservableArea[]
}

const groupAreasByLocation = (
  areas: CoreDayReservableArea[]
): GroupedAreas[] => {
  const grouped = new Map<string, GroupedAreas>()

  areas.forEach((area) => {
    const key = `${area.buildingPartLocationDetails}-${areaAddressLabel(area)}`

    if (!grouped.has(key)) {
      grouped.set(key, {
        id: key,
        address: areaAddressLabel(area),
        buildingPartLocationDetails: area.buildingPartLocationDetails,
        areas: [],
      })
    }

    grouped.get(key)!.areas.push(area)
  })

  return Array.from(grouped.values())
}

export const Storages: FC = () => {
  const { t } = useTranslation(['common', 'titles', 'storage', 'reservations'])
  const { frontPagePath } = useContractPaths()
  const { permissions } = usePermissions()
  const moduleConfig = useModuleConfig()
  const { currentContractId } = useCurrentContract()

  const [selectedArea, setSelectedArea] =
    React.useState<CoreDayReservableArea | null>(null)

  const hasRequiredPermissions =
    permissions.tenantRoles.includes('contractParty') ||
    permissions.tenantRoles.includes('payer')

  const moduleLinks = moduleConfig?.storages
  const storagesFallbackLinks: { href: string; label: string }[] = []
  if (moduleLinks?.contractDocumentLink) {
    storagesFallbackLinks.push({
      href: moduleLinks.contractDocumentLink,
      label: t('reservations:modal.commonAreaRulesLink'),
    })
  }
  if (moduleLinks?.termsOfUseDocumentLink) {
    storagesFallbackLinks.push({
      href: moduleLinks.termsOfUseDocumentLink,
      label: t('reservations:modal.commonAreaTermsLink'),
    })
  }

  const closeModal = () => {
    setSelectedArea(null)
  }

  return (
    <TitleController title={t('titles:storages')}>
      <ResidentAppView
        title={t('storage:view.title')}
        backLink={
          <ResidentAppBackLink href={frontPagePath}>
            {t('common:views.home')}
          </ResidentAppBackLink>
        }
        aside={
          <ResidentAppAsideSection>
            <ResidentAppAsideText>
              {t('storage:view.description')}
            </ResidentAppAsideText>
            <ReservationRulesAndTermsLinks
              contractId={currentContractId}
              reservableCategory="storage"
              realPropertyHierarchyId={null}
              fallbackLinks={storagesFallbackLinks}
            />
            {hasRequiredPermissions && (
              <React.Suspense fallback={<Loading variant="details" />}>
                <ExtraServiceReservationsList type="storage" />
              </React.Suspense>
            )}
          </ResidentAppAsideSection>
        }
      >
        <ResidentAppCard>
          <div className="space-y-6">
            <DayReservableAreasDataLoader include="all">
              {(dayReservableAreas, userMetadata) => {
                const groups =
                  dayReservableAreas.length > 0
                    ? groupAreasByLocation(dayReservableAreas)
                    : []

                return dayReservableAreas.length === 0 ? (
                  <Alert variant="info" className="my-8">
                    {t('storage:view.noStorageAreas')}
                  </Alert>
                ) : !hasRequiredPermissions ? (
                  <Alert variant="info" className="my-8">
                    {t('storage:view.noPermissions')}
                  </Alert>
                ) : (
                  <>
                    {groups.map((group) => (
                      <DayReservableAreasGroup
                        key={group.id}
                        group={group}
                        onOpenArea={setSelectedArea}
                        readonly={userMetadata.isImpersonator}
                      />
                    ))}
                  </>
                )
              }}
            </DayReservableAreasDataLoader>
          </div>
        </ResidentAppCard>
      </ResidentAppView>

      <DayReservableAreaModal area={selectedArea} onClose={closeModal} />
    </TitleController>
  )
}
